import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { Dialog } from 'primereact/dialog';
import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';

import ThumbnailComponent from './ThumbnailComponent';
import { getFileExtension, getFileType } from 'utils/filesUtils';
import { getTenantDetails, getValueFromCookies, removeValueFromCookies, setAppTitle } from 'utils/utils';

import DocumentViewer from './DocxViewer';
import TextFileViewer from './TextFileViewer';
import DocxViewer from './DocxViewer';
import ImageViewer from './ImageViewer';
import VideoViewer from './VideoViewer';
import PDFViewer from './PDFViewer';
import ExcelViewer from './ExcelViewer';
import { useActivityLogPreview } from 'hooks/useActivityLogPreview';
import { useHistory, useLocation } from 'react-router-dom';
import { presignedURLGet } from 'modules/FileManagerV2/api/services';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'hooks/config/queryClient';

function PreviewModal() {
  const queryClient = useQueryClient();
  const {
    modals,
    previewList,
    selectedPreviewItem,
    setSelectedPreviewItem,
    versionFileUpload,
    onMountUnMount,
    onOperationBreak,
    isClientFileManager,
    isGlobalFileManager,
    fileManagerFilters,
    caseDetails,
    query,
    getFiles,
    currentView,
    pageLimit,
  } = useFileManagerContext();
  const { showPreview, setShowPreview } = modals;

  const [filesData, setFilesData] = useState([]);
  // Lazily-resolved presigned URLs for V2 paths (keyed by object_id).
  // These are NOT pre-fetched at list-load time to avoid N API calls.
  const [resolvedUrls, setResolvedUrls] = useState({});
  const fetchingRef = useRef(new Set());

  const { addActivity } = useActivityLogPreview();
  const location = useLocation();
  const history = useHistory();

  // Fetch presigned URL on-demand for V2 paths when a file is selected for preview.
  // V2 files ("cases/", "global/", "temp/") are NOT pre-fetched at list-load time.
  useEffect(async () => {
    const filePath = selectedPreviewItem?.file_path;
    const objectId = selectedPreviewItem?.object_id;

    if (!filePath || !objectId || filePath.startsWith('public/')) return;
    if (selectedPreviewItem?.url || resolvedUrls[objectId] || fetchingRef.current.has(objectId)) return;

    fetchingRef.current.add(objectId);
    try {
      const res = await presignedURLGet(
        filePath,
        selectedPreviewItem?.version_id,
        false,
        true,
        selectedPreviewItem?.sk,
        selectedPreviewItem?.pk
      );
      const url = res?.data?.file_url;
      setResolvedUrls((prev) => ({ ...prev, [objectId]: url }));
    } catch (err) {
      console.log(err);
    } finally {
      fetchingRef.current.delete(objectId);
    }
  }, [selectedPreviewItem?.object_id]);

  useEffect(() => {
    const updateFilesData = () => {
      const isVersionUpload = versionFileUpload;
      const filteredList = isVersionUpload ? previewList : previewList.filter((item) => item.entry_type === 'file');

      const selectedIndex = selectedDataIndex(selectedPreviewItem, filteredList);

      if (selectedIndex !== -1) {
        const isSelectedPresent = filteredList.find((item) => item?.object_id === selectedPreviewItem?.object_id);

        if (!isSelectedPresent) {
          setFilesData([selectedPreviewItem, ...filteredList]);
        } else {
          setFilesData(filteredList);
        }
      } else {
        setFilesData(filteredList);
      }
    };

    updateFilesData();
  }, [previewList, versionFileUpload, selectedPreviewItem]);

  //Automated time entry logic
  useEffect(() => {
    return () => {
      if (fileManagerFilters?.case?.id || (caseDetails?.case_id && !caseDetails?.is_lead)) {
        typeof onMountUnMount === 'function' &&
          onMountUnMount('file-preview', 'unmounted', fileManagerFilters?.case?.id || caseDetails?.case_id);
      }
      setTimeout(() => {
        typeof onOperationBreak === 'function' && onOperationBreak();
      }, 3000);
    };
  }, []);

  useEffect(() => {
    // set the app tab title
    setAppTitle('File Manager', selectedPreviewItem?.display_name || '');

    // clean up function
    return () => {
      setAppTitle('File Manager');
    };
  }, [selectedPreviewItem]);

  const createActivityLog = (item) => {
    setTimeout(() => {
      const [tenantId] = getTenantDetails();
      const data = {
        event_key: 'Read',
        event_type: 'document',
        event_subtype: 'read_document',
        message: `Viewed Document: ${item?.display_name ? item?.display_name : location?.state?.case_details?.display_name}`,
        event_status: 'success',
        pk: item?.pk ? item?.pk : location?.state?.case_details?.pk,
        sk: item?.sk ? item?.sk : location?.state?.case_details?.sk,
        event_items: {
          case_id: item?.case_id ? item.case_id : location?.state?.case_details?.case_id ? location.state.case_details.case_id : '',
          tenant_id: tenantId,
          case_name: item?.case_name
            ? item.case_name
            : location?.state?.case_details?.case_name
            ? location.state.case_details.case_name
            : '',
          is_lead: item?.is_lead ? item?.is_lead : location?.state?.case_details?.is_lead,
        },
      };
      // Call addActivity and handle the response
      addActivity(data)
        .then(() => {
          // Success! Now call getFiles API
          const obj = {
            case_id: isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case?.id || '' : caseDetails?.case_id,
            selectedFolder: fileManagerFilters?.selectedFolder,
            selectedType: fileManagerFilters.fileType,
            tag: fileManagerFilters.tag,
            query: query,
            folder_id: fileManagerFilters?.selectedFolder,
            // Use the correct filter field based on file manager type
            is_read: isGlobalFileManager ? fileManagerFilters?.is_read : fileManagerFilters?.fileStatus,
          };

          // Include shared folder parameters only for client portal
          if (isClientFileManager) {
            // Include shared_folder flag if it exists in filters
            if (fileManagerFilters?.shared_folder) {
              obj.shared_folder = fileManagerFilters.shared_folder;
            }

            // Include shared_contact_list only if it exists and has values
            if (
              fileManagerFilters?.shared_contact_list &&
              Array.isArray(fileManagerFilters.shared_contact_list) &&
              fileManagerFilters.shared_contact_list.length > 0
            ) {
              obj.shared_contact_list = fileManagerFilters.shared_contact_list;
            }

            // Include folder_shared only if it's explicitly true
            if (fileManagerFilters?.folder_shared === true) {
              obj.folder_shared = fileManagerFilters.folder_shared;
            }
          }

          // Fetch the files after activity log is successfully created without global loader in this scenario
          // getFiles(obj, currentView, pageLimit, true);
          queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.list(obj.case_id) });
        })
        .catch((error) => {
          // Handle any errors during activity log creation or file fetching
          console.error('Error creating activity log or fetching files:', error);
        });
    }, 1000);
    //Automated time entry logic
    if (!item?.is_lead) {
      typeof onMountUnMount === 'function' &&
        onMountUnMount('file-preview', 'mounted', fileManagerFilters?.case?.id || caseDetails?.case_id);
    }
  };

  function findType(item) {
    return getFileType(getFileExtension(item?.display_name || item?.file_name));
  }

  const removeQueryParam = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('file_id');

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    if (getValueFromCookies('redirect_url')) {
      removeValueFromCookies('redirect_url');
    }
  };

  const handleSlideChange = (index) => {
    const fileId = index.object_id;
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('file_id', fileId);

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  function closePreviewModal() {
    removeQueryParam();
    setShowPreview(false);
    setSelectedPreviewItem();

    // Call getFiles after modal is closed
    const obj = {
      case_id: isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case?.id || '' : caseDetails?.case_id,
      selectedFolder: fileManagerFilters?.selectedFolder,
      selectedType: fileManagerFilters.fileType,
      tag: fileManagerFilters.tag,
      query: query,
      folder_id: fileManagerFilters?.selectedFolder,
      // Use the correct filter field based on file manager type
      is_read: isGlobalFileManager ? fileManagerFilters?.is_read : fileManagerFilters?.fileStatus,
    };

    // Include shared folder parameters only for client portal
    if (isClientFileManager) {
      // Include shared_folder flag if it exists in filters
      if (fileManagerFilters?.shared_folder) {
        obj.shared_folder = fileManagerFilters.shared_folder;
      }

      // Include shared_contact_list only if it exists and has values
      if (
        fileManagerFilters?.shared_contact_list &&
        Array.isArray(fileManagerFilters.shared_contact_list) &&
        fileManagerFilters.shared_contact_list.length > 0
      ) {
        obj.shared_contact_list = fileManagerFilters.shared_contact_list;
      }

      // Include folder_shared only if it's explicitly true
      if (fileManagerFilters?.folder_shared === true) {
        obj.folder_shared = fileManagerFilters.folder_shared;
      }
    }

    // getFiles(obj, currentView, pageLimit); // Fetch files after the modal is closed
    queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.lists() });
  }

  const customRenderArrowPrev = (clickHandler, hasPrev) => {
    return <i className={'pi pi-chevron-left ' + (!hasPrev && 'opacity-50')} style={arrowStyles} onClick={clickHandler} />;
  };

  const customRenderArrowNext = (clickHandler, hasNext) => {
    return <i className={'pi pi-chevron-right ' + (!hasNext && 'opacity-50')} style={arrowStyles} onClick={clickHandler} />;
  };

  const arrowStyles = {
    padding: '10px',
    border: '1px solid',
    margin: 'auto 0',
    borderRadius: '8px',
    cursor: 'pointer',
  };

  const thumbnails = filesData.map((item, index) => (
    <ThumbnailComponent key={index} type={findType(item)} url={item?.url} display_name={item?.display_name || item?.file_name} />
  ));

  function selectedDataIndex(selectedItem, data) {
    if (versionFileUpload) {
      if (selectedItem) {
        const selectedIndex = data.findIndex((item) => item?.version_id === selectedItem?.version_id);
        return selectedIndex !== -1 ? selectedIndex : 0;
      }
      return 0;
    } else {
      if (selectedItem) {
        const selectedIndex = data.findIndex((item) => item?.object_id === selectedItem?.object_id);
        return selectedIndex !== -1 ? selectedIndex : 0;
      }
      return 0;
    }
  }

  const customHeaderTemplate = (data) => {
    return (
      <div>
        <div className="d-flex justify-content-between">
          <div>
            <i className="icon-window-filled modal-close-icon pointer F-size14" onClick={data?.onHide}></i>
            {/* <i className="icon-window-filled pointer F-size14 icon-minimize"></i> */}
          </div>
          <span className="ms-3">
            {selectedPreviewItem?.display_name ? selectedPreviewItem?.display_name : location?.state?.case_details?.display_name}
          </span>
          <span></span>
        </div>
      </div>
    );
  };

  // Memoize rendering functions
  const renderViewer = React.useCallback(
    (item) => {
      const viewerProps = {
        onLoad: () => createActivityLog(item),
        item,
        isClientFileManager,
      };

      switch (item.type) {
        case 'image':
          return <ImageViewer imageUrl={item.url} title={item.caption} {...viewerProps} />;
        case 'video':
          return <VideoViewer {...viewerProps} />;
        case 'text':
          return <TextFileViewer fileUrl={item.url} {...viewerProps} />;
        case 'doc':
          return (
            <div style={{ height: '100%', width: '100%' }}>
              <DocxViewer document={item.url} {...viewerProps} />
            </div>
          );
        case 'pdf':
          return (
            <div style={{ height: '100%', width: '100%' }}>
              <PDFViewer document={item.url} {...viewerProps} />
            </div>
          );
        case 'xls':
          return (
            <div style={{ height: '100%', width: '100%' }}>
              <ExcelViewer document={item.url} {...viewerProps} />
            </div>
          );
        default:
          return (
            <div style={{ height: '100%', width: '100%' }}>
              <DocumentViewer document={item.url} {...viewerProps} />
            </div>
          );
      }
    },
    [createActivityLog, isClientFileManager]
  );

  return (
    <Dialog
      visible={showPreview}
      modal
      resizable={false}
      draggable={false}
      className="my-dialog custom-dialog preview-dialog"
      header={customHeaderTemplate}
      onHide={closePreviewModal}
      closable={false}
    >
      <Carousel
        renderThumbs={() => thumbnails}
        showThumbs={true}
        showStatus={false}
        showIndicators={false}
        centerMode={false}
        renderArrowPrev={customRenderArrowPrev}
        renderArrowNext={customRenderArrowNext}
        selectedItem={selectedDataIndex(selectedPreviewItem, filesData)}
        onChange={(e) => {
          handleSlideChange(filesData[e]);
          // setSelectedPreviewItem(filesData[e]);
        }}
        // useKeyboardArrows={true}
      >
        {filesData.map((_item, index) => {
          const itemUrl = _item?.url || resolvedUrls[_item?.object_id] || null;
          const item = { ..._item, type: findType(_item), url: itemUrl };
          const isSelected = index === selectedDataIndex(selectedPreviewItem, filesData);

          return (
            <div key={index} style={{ height: '100%', width: '100%' }}>
              {isSelected && item?.type && item?.url ? <>{renderViewer(item)}</> : null}
            </div>
          );
        })}
      </Carousel>
    </Dialog>
  );
}

export default PreviewModal;
